import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { API, axios } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { PRINCIPALE, contesto, useTraduzioni } from '../i18n';
import Icona from '../components/cliente/Icone';
import './permesso.css';

// «Rinnovo del permesso» (/rinnovo-permesso): la procedura passo per passo, per tipo di permesso. La legge il
// cliente; lo staff la apre dalla scheda del cliente con /rinnovo-permesso?cliente=<id> (e senza il parametro la
// legge lo stesso, scegliendo il tipo a mano).
//
// I TESTI NON STANNO QUI: arrivano da GET /api/testi (backend/testi_modificabili.py), che ha i testi di partenza
// scritti dentro e le correzioni dello staff fatte in Impostazioni → Testi. Così una cifra sbagliata si corregge
// senza toccare il codice. La pagina li chiede nella lingua del cliente (?lingua=…): qui si traduce solo la
// cornice (titoli, stati, bottoni, avvisi), i testi della procedura li manda già tradotti il server.
//
// I DATI DELLE PERSONE (chi ha il permesso in scadenza) arrivano da backend/permesso_soggiorno.py: per ogni
// persona del nucleo {persona_id, nome, permesso_tipo, scadenza, giorni_mancanti, stato, ricevuta, documento_id}.
// Se la rotta non risponde (404, o qualsiasi errore) la pagina funziona lo stesso: niente riquadro delle persone,
// il tipo si sceglie a mano. È l'UNICO punto da cambiare se l'indirizzo cambia.
export const ROTTA_PERMESSI = (clienteId) => `${API}/clienti/${clienteId}/permesso-soggiorno`;

// Entro quanti giorni un permesso è «in scadenza» quando il server non ci dice lo stato (il server usa 90)
export const GIORNI_IN_SCADENZA = 90;
// Gli stati del server per cui c'è qualcosa da fare ("valido" e "senza_scadenza" non si annunciano)
const STATI_DA_GUARDARE = ['in_scadenza', 'scaduto', 'in_rinnovo'];
const STATI_TRANQUILLI = ['valido', 'senza_scadenza'];

const MESI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];

// "12 dicembre 2026" da "2026-12-12" o da una data intera; quello che non si capisce non si scrive.
// `lingua` ultimo parametro, italiano di base: nelle altre lingue la stessa data la scrive Intl (i18n).
export const dataItaliana = (valore, lingua = PRINCIPALE) => {
  if (!valore) return '';
  const testo = String(valore);
  const pezzi = testo.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = pezzi ? new Date(Number(pezzi[1]), Number(pezzi[2]) - 1, Number(pezzi[3])) : new Date(testo);
  if (Number.isNaN(d.getTime())) return '';
  if (lingua === PRINCIPALE) return `${d.getDate()} ${MESI[d.getMonth()]} ${d.getFullYear()}`;
  // mezzogiorno UTC: l'ora di Roma non fa mai scivolare il giorno
  return contesto(lingua).data(new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 12)));
};

// Le persone del nucleo, comunque sia fatta la risposta (l'elenco nudo o dentro "persone")
export const personeDelNucleo = (permessi) => (Array.isArray(permessi) ? permessi : permessi?.persone) || [];

// Il permesso di cui parlare per primo: quello scaduto o che scade prima, fra quelli da guardare. null = niente da dire.
export const permessoDaRinnovare = (permessi, giorni = GIORNI_IN_SCADENZA) => {
  const daGuardare = personeDelNucleo(permessi).filter((p) => {
    if (!p || !p.scadenza) return false;
    if (STATI_DA_GUARDARE.includes(p.stato)) return true;
    if (STATI_TRANQUILLI.includes(p.stato)) return false;
    return typeof p.giorni_mancanti === 'number' && p.giorni_mancanti <= giorni;
  });
  if (!daGuardare.length) return null;
  const numero = (p) => (typeof p.giorni_mancanti === 'number' ? p.giorni_mancanti : Number.MAX_SAFE_INTEGER);
  return [...daGuardare].sort((a, b) => numero(a) - numero(b))[0];
};

// Il tipo scritto dal server ("lavoro subordinato", "LAVORO_SUBORDINATO", "studio"…) diventa uno dei nostri;
// quello che non riconosciamo finisce su "Altro", che è la scheda fatta apposta.
export const tipoRiconosciuto = (valore, tipi) => {
  const pulito = String(valore || '').toLowerCase().replace(/[\s-]+/g, '_');
  if (!pulito) return null;
  const ids = (tipi || []).map((t) => t.id);
  if (ids.includes(pulito)) return pulito;
  const quasi = ids.find((id) => pulito.includes(id) || id.includes(pulito));
  return quasi || 'altro';
};

const STATI = {
  valido: ['permesso.stati.valido', 'ok'],
  senza_scadenza: ['permesso.stati.senza_scadenza', 'ok'],
  in_scadenza: ['permesso.stati.in_scadenza', 'attenzione'],
  scaduto: ['permesso.stati.scaduto', 'male'],
  in_rinnovo: ['permesso.stati.in_rinnovo', 'ok'],
};

// "fra 34 giorni", "domani", "oggi", "scaduto da 12 giorni"
const quantoManca = (giorni, lingua = PRINCIPALE) => {
  if (typeof giorni !== 'number') return '';
  const { t, tp } = contesto(lingua);
  if (giorni < -1) return tp('permesso.plurali.scaduto_da', -giorni);
  if (giorni === -1) return t('permesso.manca.scaduto_ieri');
  if (giorni === 0) return t('permesso.manca.oggi');
  if (giorni === 1) return t('permesso.manca.domani');
  return tp('permesso.plurali.fra', giorni);
};

// Una voce «a righe» del testo diventa un elenco: una riga = un punto (la stessa regola del backend)
const righe = (testo) => String(testo || '').split('\n').map((r) => r.trim()).filter(Boolean);

const Elenco = ({ voci, numerate = false, testid }) => {
  const Lista = numerate ? 'ol' : 'ul';
  return (
    <Lista className={numerate ? 'passi' : 'punti'} data-testid={testid}>
      {voci.map((v, i) => <li key={`${i}-${v.slice(0, 24)}`}>{v}</li>)}
    </Lista>
  );
};

const Sezione = ({ icona, titolo, children, testid }) => (
  <section className="scheda-permesso" data-testid={testid}>
    <div className="ttl"><div className="i"><Icona nome={icona} /></div><h2>{titolo}</h2></div>
    {children}
  </section>
);

const RinnovoPermesso = () => {
  const { t, l: lingua } = useTraduzioni();
  const { user, userType } = useAuth();
  const { search } = useLocation();
  const [dati, setDati] = useState(null);       // GET /api/testi
  const [erroreTesti, setErroreTesti] = useState(false);
  const [persone, setPersone] = useState(null); // la rotta dell'altro agente, null finché non c'è
  const [scelto, setScelto] = useState(null);   // il tipo scelto a mano
  const [personaId, setPersonaId] = useState(null);

  // Il cliente vede sé stesso; lo staff apre la pagina dalla scheda del cliente (?cliente=<id>)
  const clienteId = useMemo(() => {
    if (userType === 'cliente') return user?.id || null;
    try { return new URLSearchParams(search || '').get('cliente'); } catch { return null; }
  }, [userType, user?.id, search]);

  useEffect(() => {
    const precedente = document.title;
    document.title = `MK Client — ${t('permesso.rinnovo')}`;
    window.scrollTo(0, 0);
    return () => { document.title = precedente; };
  }, [t]);

  // I testi si chiedono nella lingua del cliente: li traduce il server (backend/testi_modificabili.py). Se la
  // lingua non gliela sappiamo dire (o non la conosce) risponde in italiano, come prima.
  const caricaTesti = useCallback(() => {
    setErroreTesti(false);
    axios.get(`${API}/testi`, { params: { lingua } }).then((r) => setDati(r.data)).catch(() => { setDati(null); setErroreTesti(true); });
  }, [lingua]);
  useEffect(() => { caricaTesti(); }, [caricaTesti]);

  // La rotta delle persone può non esserci ancora: se risponde male non si dice niente, si sceglie il tipo a mano
  useEffect(() => {
    if (!clienteId) { setPersone(null); return; }
    let vivo = true;
    axios.get(ROTTA_PERMESSI(clienteId))
      .then((r) => { if (vivo) setPersone(personeDelNucleo(r.data)); })
      .catch(() => { if (vivo) setPersone(null); });
    return () => { vivo = false; };
  }, [clienteId]);

  const tipi = dati?.tipi || [];
  const daGuardare = useMemo(() => permessoDaRinnovare(persone), [persone]);
  const persona = useMemo(() => {
    if (!persone?.length) return null;
    return persone.find((p) => p.persona_id === personaId) || daGuardare || persone[0];
  }, [persone, personaId, daGuardare]);

  const tipo = scelto || tipoRiconosciuto(persona?.permesso_tipo, tipi) || tipi[0]?.id || 'lavoro_subordinato';
  const T = dati?.testi || {};
  const voce = (campo) => T[`permesso.${tipo}.${campo}`] || '';

  if (erroreTesti) {
    return (
      <main className="pagina-permesso" data-testid="rinnovo-permesso-guasto">
        <div className="dentro">
          <p className="vuoto-permesso" role="alert">
            {t('permesso.non_letta')}{' '}
            <button type="button" className="come-link" onClick={caricaTesti}>{t('comune.riprova')}</button>
          </p>
        </div>
      </main>
    );
  }
  if (!dati) {
    return (
      <main className="pagina-permesso" data-testid="rinnovo-permesso-attesa">
        <div className="dentro"><p className="vuoto-permesso" role="status">{t('permesso.carico')}</p></div>
      </main>
    );
  }

  return (
    <main className="pagina-permesso" data-testid="rinnovo-permesso">
      <div className="dentro">
        <header className="testa-permesso">
          <div className="k">{t('permesso.titolo')}</div>
          <h1>{t('permesso.rinnovo')}</h1>
          <p>{T['permesso.intro']}</p>
        </header>

        <p className="avviso-permesso" data-testid="avviso-permesso">
          <Icona nome="avviso" />
          <span>{T['permesso.avviso']} <b>{t('permesso.aggiornato', { data: T['permesso.aggiornato'] })}</b></span>
        </p>

        {persone?.length > 0 && (
          <section className="persone-permesso" data-testid="persone-permesso">
            <h2>{t('permesso.nucleo')}</h2>
            <div className="righe-persone">
              {persone.map((p) => {
                const [chiaveStato, colore] = STATI[p.stato] || [];
                const attiva = persona?.persona_id === p.persona_id;
                return (
                  <button type="button" key={p.persona_id || p.nome} className={`persona${attiva ? ' on' : ''}`}
                    aria-pressed={attiva} data-testid={`persona-${p.persona_id || p.nome}`}
                    onClick={() => { setPersonaId(p.persona_id); setScelto(null); }}>
                    <b>{p.nome}</b>
                    <span>
                      {p.scadenza ? t('permesso.scade_il', { data: dataItaliana(p.scadenza, lingua) }) : t('permesso.senza_data')}
                      {quantoManca(p.giorni_mancanti, lingua) ? ` · ${quantoManca(p.giorni_mancanti, lingua)}` : ''}
                    </span>
                    <span className="sotto">
                      {chiaveStato && <em className={`pallino ${colore}`}>{t(chiaveStato)}</em>}
                      {p.ricevuta ? <em className="pallino ok">{t('permesso.ricevuta_consegnata')}</em> : null}
                    </span>
                  </button>
                );
              })}
            </div>
            {persona?.documento_id && (
              <Link className="verso-documenti" to="/documenti">{t('permesso.verso_documenti')}</Link>
            )}
          </section>
        )}

        <div className="tipi-permesso" role="tablist" aria-label={t('permesso.tipo')}>
          {tipi.map((t) => (
            <button type="button" role="tab" key={t.id} aria-selected={t.id === tipo} className={t.id === tipo ? 'on' : undefined}
              data-testid={`tipo-${t.id}`} onClick={() => setScelto(t.id)}>{t.nome}</button>
          ))}
        </div>

        <div className="colonne-permesso" role="tabpanel" aria-label={tipi.find((x) => x.id === tipo)?.nome || t('permesso.generico')}>
          <div className="pila-permesso">
            <Sezione icona="fatto" titolo={t('permesso.passi')} testid="passi-permesso">
              <Elenco voci={righe(voce('passi'))} numerate testid="elenco-passi" />
            </Sezione>
            <Sezione icona="documento" titolo={t('permesso.serve')} testid="serve-permesso">
              <Elenco voci={righe(voce('serve'))} testid="elenco-serve" />
            </Sezione>
          </div>
          <div className="pila-permesso">
            <Sezione icona="luogo" titolo={t('permesso.dove')} testid="dove-permesso">
              <Elenco voci={righe(voce('dove'))} testid="elenco-dove" />
            </Sezione>
            <Sezione icona="orologio" titolo={t('permesso.tempi')} testid="tempi-permesso">
              <Elenco voci={righe(voce('tempi'))} testid="elenco-tempi" />
              <p className="nota-permesso">{T['permesso.quando']}</p>
            </Sezione>
            <Sezione icona="euro" titolo={t('permesso.mutuo_tipo')} testid="mutuo-tipo">
              <p>{voce('mutuo')}</p>
            </Sezione>
          </div>
        </div>

        <div className="colonne-permesso">
          <div className="pila-permesso">
            <Sezione icona="chiave" titolo={t('permesso.ricevuta')} testid="ricevuta-permesso">
              <Elenco voci={righe(T['permesso.ricevuta'])} testid="elenco-ricevuta" />
            </Sezione>
          </div>
          <div className="pila-permesso">
            <Sezione icona="euro" titolo={t('permesso.mutuo')} testid="mutuo-permesso">
              <Elenco voci={righe(T['permesso.mutuo'])} testid="elenco-mutuo" />
            </Sezione>
            <Sezione icona="edificio" titolo={t('permesso.banche')} testid="banche-permesso">
              <Elenco voci={righe(T['permesso.banche'])} testid="elenco-banche" />
            </Sezione>
          </div>
        </div>

        <p className="chiusa-permesso">
          {t('permesso.chiusa', { data: T['permesso.aggiornato'] })}
          {userType === 'cliente' ? t('permesso.chiusa_cliente') : ''}
        </p>
      </div>
    </main>
  );
};

export default RinnovoPermesso;
